<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import lupa from '@/assets/icons/search/lupa.svg'
import TextView from '@/ui/common/text-view/TextView.vue'
import { ScreenSize, useScreenSize } from '@/composables/use-screen-size'
import { ITextViewVariant } from '@/ui/common/text-view/types'
import { LibraryCardsGrid } from '@/ui/common/library/library-cards-grid'

import { useAgentsLibraryStore } from '@/store/task-library'
import { storeToRefs } from 'pinia'
import { IAgent, IListAgentRequestQuery } from '@/api/modules/task-library/types'
import { useQueryParams } from '@/composables/use-query-params'
import { ThreadsApi } from '@/api/modules/threads/api'
import { useRouter } from 'vue-router'
import { Routes } from '@/config/router/routes'
import { useI18n } from 'vue-i18n'
import { useThreadsStore } from '@/store/threads-store'
import { useDebounceFn } from '@vueuse/core/index'
const { t, locale } = useI18n()

const router = useRouter()

const { screenSize } = useScreenSize()

const pageSize = ref(5)

const { params, updateQueryParams } = useQueryParams<
    IListAgentRequestQuery & { agent_id?: string }
>()

const { setCurrentThread } = useThreadsStore()

const agentsLibraryStore = useAgentsLibraryStore()

const { getCategoryItems, getAgentsByCategory, clearAgentsByCategory } = agentsLibraryStore

const { agentsByCategory, agentCategoryItems, agentCategoryTotal } = storeToRefs(agentsLibraryStore)

const search = ref('')

const currentPage = ref(1)

const loading = ref(false)

watch(
    () => params.value.agent_id,
    async (value: string) => await redirectToChat(value),
)

const redirectToChat = async (agent_id: string) => {
  const { data } = await ThreadsApi.createThread({
    agent_id: agent_id,
  })

  setCurrentThread({
    thread_id: data.thread_id,
    modified_at: '',
    title: t('menu.new-chat'),
    last_message: t('menu.new-chat'),
  })

  await router.push({
    name: Routes.ChatDetail,
    params: { id: data.thread_id },
    replace: true
  })
}

const onlyOneChatFlow = async () => {
  loading.value = true

  const agentsInEveryCategoryCount = Object
      .values(agentsByCategory.value)
      .reduce<IAgent[]>((acc, categoryInfo) => [...acc, ...categoryInfo.items], [])

  if (agentsInEveryCategoryCount.length === 1) await redirectToChat(agentsInEveryCategoryCount[0].agent_id)
  loading.value = false
}

const headerVariant = computed((): ITextViewVariant => {
  switch (screenSize.value) {
    case ScreenSize.MOBILE:
      return 'roboto_28_bold'
    case ScreenSize.TABLET:
    case ScreenSize.DESKTOP_SMALL:
    case ScreenSize.DESKTOP_XL:
      return 'roboto_32_bold'
    case ScreenSize.DESKTOP_LARGE:
    default:
      return 'roboto_36_bold'
  }
})

watch(
    () => locale.value,
    async () => {
      await loadingData(params.value)
    },
)

onMounted(async () => {
  if (params.value.agent_id) {
    await redirectToChat(params.value.agent_id)
    return
  }

  await loadingData(params.value)
  await onlyOneChatFlow()

})

const loadingData = async (params?: Partial<IListAgentRequestQuery>) => {
  loading.value = true

  updateQueryParams({ ...params })

  search.value = params?.search || ''
  currentPage.value = params?.page || 1

  agentCategoryItems.value.map((c, index) => clearAgentsByCategory(c.code))

  await getCategoryItems({ ...params, limit: pageSize.value })

  await Promise.all(
      agentCategoryItems.value.map((c, index) =>
          getAgentsByCategory(c.code, { page: 1, search: search.value, limit: 3 }),
      ),
  )

  loading.value = false
}

const searchFetch = async () => {
  await loadingData({ search: search.value })

  updateQueryParams({
    ...params.value,
    search: search.value,
    page: 1,
  })
}

const handleKeyDown = async ({ key }: KeyboardEvent) => key === 'Enter' && searchFetch()

const handleChangePage = async (page: number) => {
  await loadingData({
    ...params.value,
    page: page,
  })
}

const handleShowMore = ({ category }: any) => {
  const data = agentsByCategory.value[category]
  const page = data?.page || 1

  getAgentsByCategory(category, { page: page + 1, search: search.value, limit: 3 }, true)
}

const isShowPagination = computed(() => {
  return agentCategoryTotal.value / pageSize.value > 1
})

const isShowNoItems = computed(() => {
  return Object.keys(agentCategoryItems.value).length === 0 && !loading.value
})

const debouncedSearch = useDebounceFn(searchFetch, 500)

watch(() => search.value, debouncedSearch)
</script>

<template>
  <div class="LibraryPage">
    <div class="LibraryPage__wrapper">
      <TextView :variant="headerVariant" class="LibraryPage__header">
        {{ t('page.assistant-select-title') }}
      </TextView>

      <TextView variant="roboto_14_regular" color="gray_60" class="LibraryPage__label">
        {{ t('page.assistant-select-text') }}
      </TextView>

      <!--      TODO refactor input-->

      <a-input
          v-model:value="search"
          :placeholder="t('ui.search')"
          class="LibraryPage__input"
          size="large"
          @keydown="handleKeyDown"
      >
        <template #suffix>
          <img :src="lupa" alt="Лупа" @click="searchFetch" class="LibraryPage__lupa" />
        </template>
      </a-input>
      <div v-if="isShowNoItems">
        {{ t('ui.agents-search-no-result') }}
      </div>
      <div class="LibraryPage__content">
        <LibraryCardsGrid v-if="!loading" :data="agentsByCategory" @showmore="handleShowMore" />
        <a-space v-else>
          <a-spin size="large" />
        </a-space>
      </div>
      <div class="LibraryPage__pagination">
        <a-pagination
            v-if="isShowPagination"
            v-model:current="currentPage"
            :total="agentCategoryTotal"
            :page-size="pageSize"
            :show-size-changer="false"
            class="custom-pagination"
            @change="handleChangePage"
            :responsive="true"
        />
      </div>
    </div>
  </div>
</template>
<style lang="css">
.LibraryPage {
  min-height: calc(100vh - 56px);
  height: 100%;
  background-color: var(--gray_0);
}

.LibraryPage__wrapper {
  display: flex;
  margin: 0 auto;
  max-width: 808px;
  padding-bottom: 56px;
  align-items: center;
  align-content: start;
  height: 100%;
  flex-direction: column;
}

.LibraryPage__content {
  display: flex;
  flex-grow: 1;
}

.LibraryPage__pagination {
  display: flex;
  align-items: flex-end;
  height: 88px;
}

.LibraryPage__label {
  text-align: center;
  margin-bottom: 32px;
}

.LibraryPage__header {
  margin-bottom: 16px;
}

.LibraryPage__input {
  margin-bottom: 90px;
}

@media (max-width: 767px) {
  .LibraryPage {
    padding: 48px 24px;
  }

  .LibraryPage__header {
    margin-top: 48px;
    margin-bottom: 12px;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .LibraryPage {
    padding: 0 40px;
  }

  .LibraryPage__header {
    margin-top: 40px;
  }
}

@media (min-width: 1024px) and (max-width: 1279px) {
  .LibraryPage {
    padding: 0 84px;
  }

  .LibraryPage__header {
    margin-top: 56px;
  }
}

/* Desktop Large */
@media (min-width: 1280px) and (max-width: 1920px) {
  .LibraryPage {
    padding: 0 164px;
  }

  .LibraryPage__header {
    margin-top: 60px;
  }
}

@media (min-width: 1920px) {
  .LibraryPage__header {
    margin-top: 80px;
  }
}

.LibraryPage__lupa {
  cursor: pointer;
}
</style>
